const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const dialogOpeners = new WeakMap();

const syncDialogTrigger = (dialog, expanded) => {
  if (!dialog?.id) return;
  const selector = dialog.matches('[data-cart-dialog]') ? '[data-cart-open]' : `[data-dialog-open="${dialog.id}"]`;
  document.querySelectorAll(selector).forEach((trigger) => {
    trigger.setAttribute('aria-expanded', String(expanded));
  });
};

const syncScrollLock = () => {
  const isLocked = Boolean(document.querySelector('dialog[data-modal][open]'));
  document.body.classList.toggle('is-locked', isLocked);
  if (isLocked) {
    const compensation = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--scroll-lock-compensation', `${compensation}px`);
  } else {
    document.documentElement.style.removeProperty('--scroll-lock-compensation');
  }
};

const announce = (message) => {
  const region = document.querySelector('[data-live-region]');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
};

const openDialog = (dialog, opener) => {
  if (!dialog || dialog.open) return;
  dialogOpeners.set(dialog, opener);
  dialog.dataset.openerId = opener?.id || '';
  dialog.showModal();
  syncDialogTrigger(dialog, true);
  syncScrollLock();
  const focusTarget = dialog.querySelector('[autofocus]') || dialog.querySelector(focusableSelector);
  focusTarget?.focus();
};

const closeDialog = (dialog) => {
  if (!dialog?.open) return;
  dialog.close();
};

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (dialog) {
      event.preventDefault();
      openDialog(dialog, opener);
    }
  }

  const closer = event.target.closest('[data-dialog-close]');
  if (closer) closeDialog(closer.closest('dialog'));

  const detailsCloser = event.target.closest('[data-details-close]');
  if (detailsCloser) detailsCloser.closest('details')?.removeAttribute('open');

  const cartLink = event.target.closest('[data-cart-open]');
  if (cartLink && document.querySelector('cart-drawer[data-enabled="true"]')) {
    event.preventDefault();
    const drawer = document.querySelector('[data-cart-dialog]');
    openDialog(drawer, cartLink);
  }
});

document.addEventListener('click', (event) => {
  const dialog = event.target.closest('dialog[data-modal]');
  if (!dialog || event.target !== dialog) return;
  const bounds = dialog.getBoundingClientRect();
  const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
  if (!inside) closeDialog(dialog);
});

document.addEventListener('close', (event) => {
  if (!event.target.matches('dialog[data-modal]')) return;
  syncDialogTrigger(event.target, false);
  syncScrollLock();
  const opener = dialogOpeners.get(event.target) || (event.target.dataset.openerId ? document.getElementById(event.target.dataset.openerId) : null);
  opener?.focus();
}, true);

document.addEventListener('cancel', (event) => {
  if (!event.target.matches('dialog[data-modal]')) return;
  event.preventDefault();
  closeDialog(event.target);
}, true);

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-submit-on-change]')) event.target.form?.requestSubmit();
});

const enhanceLanguageLocalizationForms = (root = document) => {
  root.querySelectorAll('[data-language-localization-form]').forEach((form) => {
    if (form.dataset.enhanced === 'true') return;
    const select = form.querySelector('select[name="language_code"], select[name="country_code"]');
    if (!select) return;
    const initialValue = select.value;
    let submitTimer;
    let pointerSelection = false;
    const submitForm = () => {
      if (select.value === initialValue) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    };
    select.addEventListener('pointerdown', () => {
      pointerSelection = true;
      form.removeAttribute('data-keyboard-selection');
    });
    select.addEventListener('keydown', () => {
      pointerSelection = false;
    });
    select.addEventListener('change', () => {
      window.clearTimeout(submitTimer);
      if (pointerSelection) submitTimer = window.setTimeout(submitForm, 400);
      else form.setAttribute('data-keyboard-selection', 'true');
      pointerSelection = false;
    });
    form.dataset.enhanced = 'true';
  });
};

enhanceLanguageLocalizationForms();

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const pauseAutoplayVideos = (root = document) => {
  root.querySelectorAll('video[autoplay]').forEach((video) => {
    video.pause();
    video.removeAttribute('autoplay');
  });
};

if (reducedMotionQuery.matches) pauseAutoplayVideos();
reducedMotionQuery.addEventListener('change', (event) => {
  if (event.matches) pauseAutoplayVideos();
});

const focusFormStatus = (root = document) => {
  root.querySelector('[data-form-status][tabindex="-1"]')?.focus();
};

focusFormStatus();

document.addEventListener('shopify:section:load', (event) => {
  enhanceLanguageLocalizationForms(event.target);
  focusFormStatus(event.target);
  if (reducedMotionQuery.matches) pauseAutoplayVideos(event.target);
});

document.addEventListener('click', (event) => {
  const disclosure = event.target.closest('[data-nav-disclosure]');
  if (!disclosure) {
    document.querySelectorAll('[data-nav-disclosure][open]').forEach((item) => item.removeAttribute('open'));
    return;
  }
  if (!event.target.closest('summary')) return;
  window.setTimeout(() => {
    if (!disclosure.open) return;
    document.querySelectorAll('[data-nav-disclosure][open]').forEach((item) => {
      if (item !== disclosure) item.removeAttribute('open');
    });
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const dialog = document.querySelector('dialog[data-modal][open]');
  if (dialog) {
    event.preventDefault();
    closeDialog(dialog);
    return;
  }
  const disclosure = document.querySelector('[data-nav-disclosure][open]');
  if (!disclosure) return;
  event.preventDefault();
  disclosure.removeAttribute('open');
  disclosure.querySelector('summary')?.focus();
});

class SiteHeader extends HTMLElement {
  connectedCallback() {
    if (!this.classList.contains('site-header--transparent')) return;
    this.updateScrollState = () => this.classList.toggle('is-scrolled', window.scrollY > 24);
    this.updateScrollState();
    window.addEventListener('scroll', this.updateScrollState, { passive: true });
  }

  disconnectedCallback() {
    if (this.updateScrollState) window.removeEventListener('scroll', this.updateScrollState);
  }
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.hint = this.querySelector('[data-predictive-hint]');
    if (!this.input || !this.results) return;
    this.abortController = null;
    this.timer = null;
    this.requestId = 0;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 180);
    });
    this.addEventListener('keydown', (event) => this.navigate(event));
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.abortController?.abort();
  }

  setState(state, message = '') {
    this.dataset.state = state;
    this.results.dataset.state = state;
    this.results.setAttribute('aria-busy', String(state === 'loading'));
    this.hint?.toggleAttribute('hidden', state !== 'idle');
    if (!message) return;
    this.results.replaceChildren();
    const status = document.createElement('p');
    status.className = 'predictive-results__status';
    status.textContent = message;
    this.results.append(status);
  }

  navigate(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog(this.closest('dialog'));
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const options = [...this.results.querySelectorAll('a')];
    if (!options.length) return;
    event.preventDefault();
    const currentIndex = options.indexOf(document.activeElement);
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex < 0 ? (direction > 0 ? 0 : options.length - 1) : (currentIndex + direction + options.length) % options.length;
    options[nextIndex].focus();
  }

  async search() {
    const query = this.input.value.trim();
    const requestId = ++this.requestId;
    if (query.length < 2) {
      this.results.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      this.setState('idle');
      return;
    }
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setState('loading', this.dataset.loadingMessage);
    const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error('Search request failed');
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
      if (requestId !== this.requestId) return;
      this.results.innerHTML = html?.innerHTML || '';
      this.input.setAttribute('aria-expanded', 'true');
      this.setState('ready');
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === this.requestId) {
        this.input.setAttribute('aria-expanded', 'false');
        this.setState('error', this.dataset.errorMessage);
      }
    }
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;
    this.addEventListener('click', (event) => {
      const control = event.target.closest('[data-product-quantity-change]');
      if (!control || cartBusy) return;
      const input = this.querySelector('input[name="quantity"]');
      if (input) input.value = Math.max(1, Number(input.value || 1) + Number(control.dataset.productQuantityChange));
    });
    if (this.dataset.ajax === 'true') this.form.addEventListener('submit', (event) => this.submit(event));
  }

  async submit(event) {
    if (event.submitter && event.submitter.name !== 'add') return;
    event.preventDefault();
    if (cartBusy || cartNeedsReview || this.submitting) return;
    const button = this.form.querySelector('[name="add"]');
    const opener = this.cartOpener || button;
    this.cartOpener = null;
    if (button?.disabled) return;
    const body = new FormData(this.form);
    const config = getCartConfig();
    body.set('sections', cartSectionIds().join(','));
    body.set('sections_url', config.cartUrl);
    const errorNode = this.querySelector('[data-product-error]');
    if (errorNode) { errorNode.textContent = ''; errorNode.hidden = true; }
    this.submitting = true;
    setCartBusy(true);
    announceCart(config.loadingMessage);
    try {
      const payload = await cartRequest(this.form.action, {
        method: 'POST', headers: { Accept: 'application/json' }, body
      });
      await refreshCartDrawer(true, opener, payload.sections);
      announceCart(this.dataset.successMessage);
    } catch (error) {
      // Never retry an add: a lost response can still represent a successful mutation.
      const message = config.reviewMessage;
      try { await refreshCartDrawer(false); } catch (_) { cartNeedsReview = true; }
      showCartError(message, true);
      if (errorNode) { errorNode.textContent = message; errorNode.hidden = false; }
      if (this.isConnected) openDialog(document.querySelector('[data-cart-dialog]'), opener);
      announceCart(message);
    } finally {
      this.submitting = false;
      setCartBusy(false);
    }
  }
}

class CartSurface extends HTMLElement {
  connectedCallback() {
    this.enhance();
    if (this.initialized) return;
    this.initialized = true;
    this.addEventListener('click', (event) => {
      const control = event.target.closest('[data-cart-change]');
      if (!control) return;
      event.preventDefault();
      this.change(control, Number(control.dataset.quantity));
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-quantity]')) this.commitQuantity(event.target);
    });
    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-cart-quantity]')) event.target.setCustomValidity('');
      if (event.target.matches('[name="note"]')) {
        document.querySelectorAll('cart-drawer [name="note"], cart-page [name="note"]').forEach((input) => {
          if (input !== event.target) input.value = event.target.value;
        });
      }
    });
    this.addEventListener('submit', (event) => {
      if (cartBusy || cartNeedsReview) event.preventDefault();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-cart-quantity]')) {
        event.preventDefault();
        this.commitQuantity(event.target);
      }
      const dialog = event.target.closest('[data-cart-dialog]');
      if (event.key !== 'Tab' || !dialog?.open) return;
      const stops = [...dialog.querySelectorAll(focusableSelector)].filter((node) => node.tabIndex >= 0 && node.getClientRects().length);
      const first = stops[0], last = stops[stops.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
  }

  enhance() {
    this.querySelector('[data-cart-content]')?.setAttribute('data-enhanced', '');
    this.querySelectorAll('[data-cart-change][hidden]').forEach((node) => { node.hidden = false; });
  }

  commitQuantity(input) {
    const quantity = Number(input.value);
    const minimum = Number(input.dataset.minimum || 1);
    if (!Number.isInteger(quantity) || input.value === '' || quantity < 0 || (quantity > 0 && quantity < minimum) || !input.checkValidity()) {
      showCartError(input.dataset.quantityError);
      announceCart(input.dataset.quantityError);
      input.value = input.dataset.currentQuantity;
      input.setCustomValidity('');
      return;
    }
    if (quantity !== Number(input.dataset.currentQuantity)) this.change(input, quantity);
  }

  async change(control, quantity) {
    if (cartBusy || cartNeedsReview || control.disabled) return;
    const line = control.closest('[data-cart-line]');
    if (!line || !Number.isInteger(quantity) || quantity < 0) return;
    const focus = { key: line.dataset.key, index: line.dataset.lineIndex, action: control.dataset.cartAction };
    const wasOpen = Boolean(this.querySelector('dialog[open]'));
    setCartBusy(true);
    clearCartErrors();
    announceCart(getCartConfig().loadingMessage);
    try {
      const payload = await cartRequest(this.dataset.changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: focus.key, quantity, sections: cartSectionIds(), sections_url: getCartConfig().cartUrl })
      });
      await refreshCartDrawer(false, null, payload.sections);
      announceCart(getCartConfig().updatedMessage);
    } catch (error) {
      // Reconcile with Shopify after rejection or connection loss; never replay the mutation.
      try { await refreshCartDrawer(false); } catch (_) { cartNeedsReview = true; }
      const message = error.rejected && !cartNeedsReview ? getCartConfig().errorMessage : getCartConfig().reviewMessage;
      showCartError(message, cartNeedsReview || !error.rejected);
      announceCart(message);
    } finally {
      setCartBusy(false);
      if (this.isConnected && (this.matches('cart-page') || (wasOpen && this.querySelector('dialog[open]')))) {
        const row = this.querySelector(`[data-key="${CSS.escape(focus.key)}"]`) || this.querySelector(`[data-line-index="${focus.index}"]`);
        const target = row?.querySelector(`[data-cart-action="${focus.action}"]:not([disabled])`) || this.querySelector('.bag-empty a, [data-dialog-close], .bag-item__title a');
        target?.focus({ preventScroll: true });
      }
    }
  }
}

class CartDrawer extends CartSurface {
  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.enabled !== 'true') return;
    document.querySelectorAll('[data-cart-open]').forEach((node) => {
      node.setAttribute('aria-controls', 'CartDrawer');
      node.setAttribute('aria-haspopup', 'dialog');
      node.setAttribute('aria-expanded', String(Boolean(this.querySelector('dialog[open]'))));
    });
  }
}
class CartPage extends CartSurface {}

class ProductVariants extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-variant-select]');
    if (!this.select) return;
    this.optionInputs = [...this.querySelectorAll('[data-option-input]')];
    this.optionInputs.forEach((input) => input.addEventListener('change', () => this.selectVariant()));
    this.select.addEventListener('change', () => this.update());
    this.dataset.enhanced = 'true';
    this.select.hidden = true;
    this.update(false);
  }

  selectVariant() {
    const selectedValues = [...this.querySelectorAll('[data-option-input]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
    const variant = [...this.select.options].find((option) => {
      try {
        return JSON.parse(option.dataset.options).every((value, index) => value === selectedValues[index]);
      } catch (_) {
        return false;
      }
    });
    this.select.value = variant?.value || '';
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  update(navigate = true) {
    const option = this.select.selectedOptions[0];
    const available = option?.dataset.available === 'true';
    const preorder = available && option?.dataset.preorder === 'true';
    const addText = preorder ? (this.dataset.preorderLabel || this.dataset.addLabel) : this.dataset.addLabel;
    const availableText = preorder ? (this.dataset.preorderStatus || this.dataset.availableLabel) : this.dataset.availableLabel;
    const price = this.querySelector('[data-current-price]');
    const compare = this.querySelector('[data-compare-price]');
    const availability = this.querySelector('[data-product-availability]');
    const availabilityText = this.querySelector('[data-availability-text]');
    const addButton = this.querySelector('[data-add-button]');
    const addLabel = this.querySelector('[data-add-label]');
    const priceGroup = this.querySelector('[data-product-price]');
    if (priceGroup) priceGroup.hidden = !option;
    if (price) price.textContent = option?.dataset.price || '';
    if (compare) {
      compare.textContent = option?.dataset.comparePrice || '';
      compare.hidden = option?.dataset.hasCompare !== 'true';
    }
    availability?.classList.toggle('is-unavailable', !available);
    const unavailableLabel = option ? this.dataset.soldOutLabel : this.dataset.unavailableLabel;
    if (availabilityText) availabilityText.textContent = available ? availableText : unavailableLabel;
    if (addButton) addButton.disabled = !available;
    if (addLabel) addLabel.textContent = available ? addText : unavailableLabel;
    this.querySelectorAll('[data-preorder-only]').forEach((node) => {
      if (node.matches('input')) node.disabled = !preorder;
      else node.hidden = !preorder;
    });
    // The in-stock dispatch time never sits next to a pre-order note.
    this.querySelectorAll('[data-instock-only]').forEach((node) => {
      node.hidden = preorder || !available;
    });
    this.updateOptionAvailability();
    if (navigate && option?.dataset.url) window.history.replaceState({}, '', option.dataset.url);
    if (navigate && option?.dataset.mediaId) this.closest('[data-product-section]')?.querySelector('[data-gallery]')?.showMedia(option.dataset.mediaId);
    this.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { available, preorder, price: option?.dataset.price || '', addLabel: available ? addText : unavailableLabel }
    }));
  }

  updateOptionAvailability() {
    const selected = [...this.querySelectorAll('[data-option-input]:checked')];
    this.optionInputs.forEach((input) => {
      const position = Number(input.dataset.optionPosition) - 1;
      const available = [...this.select.options].some((option) => {
        if (option.dataset.available !== 'true') return false;
        try {
          const values = JSON.parse(option.dataset.options);
          return values[position] === input.value && selected.every((choice) => {
            const prior = Number(choice.dataset.optionPosition) - 1;
            return prior >= position || values[prior] === choice.value;
          });
        } catch (_) { return false; }
      });
      input.disabled = !available;
      input.nextElementSibling?.classList.toggle('is-unavailable', !available);
    });
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-gallery-scroller]');
    this.items = [...this.querySelectorAll('[data-gallery-item]')];
    this.current = this.querySelector('[data-gallery-current]');
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom-index]');
      if (!trigger) return;
      const zoom = document.getElementById(trigger.dataset.dialogOpen)?.closest('product-zoom');
      zoom?.show(Number(trigger.dataset.zoomIndex));
    });
    if (this.scroller && this.current) this.scroller.addEventListener('scroll', () => this.updateCounter(), { passive: true });
  }

  updateCounter() {
    const closest = this.items.reduce((best, item, index) => {
      const distance = Math.abs(item.getBoundingClientRect().left - this.scroller.getBoundingClientRect().left);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Infinity });
    this.current.textContent = String(closest.index + 1).padStart(2, '0');
  }

  showMedia(mediaId) {
    const item = this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    item?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }
}

class ProductZoom extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.items = [...this.querySelectorAll('[data-zoom-item]')];
    this.currentNode = this.querySelector('[data-zoom-current]');
    this.currentIndex = 0;
    this.querySelector('[data-zoom-previous]')?.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.move(1));
    this.dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.move(-1);
      if (event.key === 'ArrowRight') this.move(1);
    });
  }

  show(index) {
    this.currentIndex = Number.isFinite(index) ? index : 0;
    this.render();
  }

  move(direction) {
    if (this.items.length < 2) return;
    this.currentIndex = (this.currentIndex + direction + this.items.length) % this.items.length;
    this.render();
  }

  render() {
    this.items.forEach((item, index) => item.classList.toggle('is-hidden', index !== this.currentIndex));
    if (this.currentNode) this.currentNode.textContent = String(this.currentIndex + 1).padStart(2, '0');
  }
}

class MobilePurchaseBar extends HTMLElement {
  connectedCallback() {
    this.mainButton = document.querySelector(this.dataset.mainButtonSelector);
    this.stickyButton = this.querySelector('[data-sticky-add]');
    if (!this.mainButton || !this.stickyButton) return;
    this.stickyButton.addEventListener('click', () => {
      if (this.mainButton.disabled) return;
      const form = this.mainButton.closest('product-form');
      if (form) form.cartOpener = this.stickyButton;
      this.mainButton.click();
    });
    this.inert = true;
    this.observer = new IntersectionObserver(([entry]) => {
      this.pastPurchase = entry.intersectionRatio < 0.2 && entry.boundingClientRect.top < 0;
      this.syncVisibility();
    }, { threshold: [0, 0.2] });
    this.observer.observe(this.mainButton);
    this.footer = document.querySelector('.site-footer');
    if (this.footer) {
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerVisible = entry.isIntersecting;
        if (this.footerVisible && this.contains(document.activeElement)) {
          this.footer.querySelector('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus({ preventScroll: true });
        }
        this.syncVisibility();
      }, { threshold: 0 });
      this.footerObserver.observe(this.footer);
    }
    document.addEventListener('variant:change', this.variantHandler = (event) => {
      if (event.target !== this.mainButton.closest('product-variants')) return;
      const price = this.querySelector('[data-sticky-price]');
      if (price) price.textContent = event.detail.price;
      this.classList.toggle('is-unavailable', !event.detail.available);
      this.stickyButton.disabled = !event.detail.available;
      this.stickyButton.textContent = event.detail.addLabel;
      this.syncVisibility();
    });
  }

  syncVisibility() {
    const visible = Boolean(this.pastPurchase) && !this.footerVisible && !this.classList.contains('is-unavailable');
    this.classList.toggle('is-visible', visible);
    this.inert = !visible;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.footerObserver?.disconnect();
    if (this.variantHandler) document.removeEventListener('variant:change', this.variantHandler);
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      this.load();
    }, { rootMargin: '240px 0px' });
    observer.observe(this);
  }

  async load(url = this.dataset.url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.hidden = true;
        return;
      }
      const text = await response.text();
      const next = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
      if (next?.innerHTML.trim()) {
        this.innerHTML = next.innerHTML;
        this.classList.add('is-loaded');
      } else if (this.dataset.fallbackUrl && url !== this.dataset.fallbackUrl) {
        // Complementary pairings come from Search & Discovery; until they exist, fall back to related pieces.
        this.load(this.dataset.fallbackUrl);
      } else {
        this.hidden = true;
      }
    } catch (_) {
      this.hidden = true;
    }
  }
}

// One in-flight mutation across PDP, drawer and cart page. No optimistic money arithmetic.
let cartBusy = false;
let cartNeedsReview = false;
const cartDisabledStates = new WeakMap();
const getCartConfig = () => document.querySelector('cart-drawer')?.dataset || {};
const cartSectionIds = () => [...document.querySelectorAll('cart-drawer, cart-page')].map((node) => node.dataset.sectionId).filter(Boolean);

const announceCart = (message) => {
  const status = document.querySelector('dialog[data-cart-dialog][open] [data-cart-status]');
  if (!status) { announce(message); return; }
  // Modal content outside the dialog is inert, including the global live region.
  status.textContent = '';
  window.setTimeout(() => { status.textContent = message; }, 50);
};

const setCartBusy = (busy) => {
  cartBusy = busy;
  document.querySelectorAll('[data-cart-content]').forEach((node) => node.setAttribute('aria-busy', String(busy)));
  document.querySelectorAll('[data-cart-change], [data-cart-quantity], .bag-checkout, .bag-update, product-form [name="add"], [data-sticky-add], product-form select, product-form [data-option-input]').forEach((node) => {
    if (busy) {
      if (!cartDisabledStates.has(node)) cartDisabledStates.set(node, node.disabled || false);
      if ('disabled' in node) node.disabled = true;
      node.setAttribute('aria-disabled', 'true');
      if (node.matches('[name="add"], [data-sticky-add]')) node.setAttribute('aria-busy', 'true');
    } else {
      if ('disabled' in node) node.disabled = cartNeedsReview || (cartDisabledStates.get(node) ?? node.disabled);
      if (cartNeedsReview) node.setAttribute('aria-disabled', 'true');
      else node.removeAttribute('aria-disabled');
      node.removeAttribute('aria-busy');
      cartDisabledStates.delete(node);
    }
  });
};

const cartRequest = async (url, options = {}) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (!response.ok) { const error = new Error('Cart request rejected'); error.rejected = response.status >= 400 && response.status < 500; throw error; }
    return await response.json();
  } finally { window.clearTimeout(timer); }
};

const clearCartErrors = () => {
  document.querySelectorAll('.bag-error').forEach((node) => { node.hidden = true; });
};
const showCartError = (message, review = false) => {
  document.querySelectorAll('.bag-error').forEach((node) => {
    node.hidden = false;
    node.querySelector('[data-cart-error]').textContent = message;
    node.querySelector('[data-cart-review]').hidden = !review;
  });
};

const refreshCartDrawer = async (shouldOpen, opener = document.activeElement, sections) => {
  const surfaces = [...document.querySelectorAll('cart-drawer, cart-page')];
  if (!surfaces.length) return;
  if (!sections || surfaces.some((node) => !sections[node.dataset.sectionId])) {
    sections = await cartRequest(`${getCartConfig().cartUrl}?sections=${cartSectionIds().join(',')}`);
  }
  // Validate all fragments before replacing anything, avoiding mismatched totals.
  const replacements = surfaces.map((surface) => {
    const markup = sections[surface.dataset.sectionId];
    const next = markup && new DOMParser().parseFromString(markup, 'text/html').querySelector(surface.localName);
    if (!next?.querySelector('[data-cart-content]')) throw new Error('Cart section unavailable');
    return { surface, next };
  });
  replacements.forEach(({ surface, next }) => {
    const content = surface.querySelector('[data-cart-content]');
    const scroll = surface.querySelector('[data-cart-scroll]')?.scrollTop || 0;
    const note = content.querySelector('[name="note"]')?.value;
    content.innerHTML = next.querySelector('[data-cart-content]').innerHTML;
    if (note !== undefined && content.querySelector('[name="note"]')) content.querySelector('[name="note"]').value = note;
    if (surface.querySelector('[data-cart-scroll]')) surface.querySelector('[data-cart-scroll]').scrollTop = scroll;
    Object.assign(surface.dataset, next.dataset);
    surface.enhance();
  });
  const current = document.querySelector('cart-drawer');
  document.querySelectorAll('[data-cart-count]').forEach((node) => {
    node.textContent = current.dataset.count;
    node.hidden = current.dataset.count === '0';
  });
  document.querySelectorAll('[data-cart-open]').forEach((node) => node.setAttribute('aria-label', current.dataset.cartLabel));
  clearCartErrors();
  if (shouldOpen) openDialog(current.querySelector('[data-cart-dialog]'), opener);
};

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-cookie-preferences]');
  if (!trigger) return;
  const banner = window.privacyBanner;
  if (typeof banner?.showPreferences === 'function') {
    event.preventDefault();
    banner.showPreferences();
    return;
  }
  const privacy = window.Shopify?.customerPrivacy;
  if (typeof privacy?.showPreferences === 'function') {
    event.preventDefault();
    privacy.showPreferences();
  }
});

customElements.define('site-header', SiteHeader);
customElements.define('predictive-search', PredictiveSearch);
customElements.define('product-form', ProductForm);
customElements.define('cart-drawer', CartDrawer);
customElements.define('cart-page', CartPage);
customElements.define('product-variants', ProductVariants);
customElements.define('product-gallery', ProductGallery);
customElements.define('product-zoom', ProductZoom);
customElements.define('mobile-purchase-bar', MobilePurchaseBar);
customElements.define('product-recommendations', ProductRecommendations);

/* ---------------------------------------------------------------------------
 * Reduced motion: stop autoplay media.
 *
 * WCAG 2.2.2 requires a mechanism to pause moving content. The stylesheet
 * neutralises CSS animations under prefers-reduced-motion, but video playback
 * is not covered by CSS. Removing the autoplay attribute stops playback and
 * leaves the poster frame visible, so the section still renders its artwork.
 *
 * Runs on load and reacts if the preference changes mid-session.
 * ------------------------------------------------------------------------- */
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const stopAutoplayMedia = () => {
  if (!prefersReducedMotion.matches) return;
  document.querySelectorAll('video[autoplay]').forEach((video) => {
    video.removeAttribute('autoplay');
    video.pause();
  });
};

stopAutoplayMedia();
prefersReducedMotion.addEventListener('change', stopAutoplayMedia);
document.addEventListener('shopify:section:load', stopAutoplayMedia);
